import {
  hasPendingIconArt,
  iconArtSettled,
} from '~/client/src/shared/components/SitemapHelpers/utils/markerSpec'

import { hasPendingMapArt, mapArtSettled } from './mapArtPending'

// Keyed by map instance: a capture and the code that pauses for it share no store, only the map.
// A capture and a plain fade hold share the one saved fade, so neither restores it under the other.
const held = new WeakMap<
  mapboxgl.Map,
  { holds: number; captures: number; savedFade: number }
>()

type FadeMap = { _fadeDuration: number }

function holdFade(map: mapboxgl.Map, isCapture: boolean): () => void {
  const entry = held.get(map) ?? {
    holds: 0,
    captures: 0,
    savedFade: (map as unknown as FadeMap)._fadeDuration,
  }
  if (entry.holds === 0) {
    ;(map as unknown as FadeMap)._fadeDuration = 0
  }
  entry.holds++
  if (isCapture) entry.captures++
  held.set(map, entry)

  let isReleased = false
  return () => {
    if (isReleased) {
      return
    }
    isReleased = true
    entry.holds--
    if (isCapture) entry.captures--
    if (entry.holds === 0) {
      ;(map as unknown as FadeMap)._fadeDuration = entry.savedFade
      held.delete(map)
      map.triggerRepaint()
    }
  }
}

/**
 * A per-frame animation keeps 'idle' from ever firing. Also zeroes the symbol fade: an
 * icon-allow-overlap pin stays invisible for a fade window after a camera jump (STX1-875).
 */
export const suspendMapAnimations = (map: mapboxgl.Map): (() => void) =>
  holdFade(map, true)

// The fade alone, for placement that must land at once. Not a capture: nothing pauses for it.
export const suspendSymbolFade = (map: mapboxgl.Map): (() => void) =>
  holdFade(map, false)

export const isMapAnimationSuspended = (map: mapboxgl.Map): boolean =>
  (held.get(map)?.captures ?? 0) > 0

type SourceCaches = Record<string, { hasTransition(): boolean }>

// Root hasTransitions() never checks an imported basemap's fragment, so 'idle' can fire mid-fade
// with nothing scheduled to finish it.
const isBasemapFading = (map: mapboxgl.Map): boolean =>
  Object.values(
    (map as unknown as { style?: { _mergedSourceCaches?: SourceCaches } }).style
      ?._mergedSourceCaches ?? {},
  ).some(cache => cache.hasTransition())

/** True once the frame is complete for this camera; false on timeout or removal, never "close enough". */
/**
 * Whether the map drew a finished frame within the budget. A frame still showing a sign's tone
 * tile, or a text markup waiting for its font or drawn soft, is not finished: the art is waited
 * for, then the frame it swaps into.
 */
export async function waitForMapFrame(
  map: mapboxgl.Map,
  timeoutMs: number,
): Promise<boolean> {
  const deadline = Date.now() + timeoutMs
  for (;;) {
    if (!(await waitForRenderedFrame(map, deadline - Date.now()))) {
      return false
    }
    if (!hasPendingIconArt() && !hasPendingMapArt()) {
      return true
    }
    const isSettled = await Promise.race([
      Promise.all([iconArtSettled(), mapArtSettled()]).then(() => true),
      new Promise<boolean>(resolve =>
        setTimeout(() => resolve(false), Math.max(0, deadline - Date.now())),
      ),
    ])
    if (!isSettled) {
      return false
    }
  }
}

function waitForRenderedFrame(
  map: mapboxgl.Map,
  timeoutMs: number,
): Promise<boolean> {
  return new Promise<boolean>(resolve => {
    let isIdle = false
    const settle = (isReady: boolean): void => {
      clearTimeout(timer)
      map.off('idle', onIdle)
      map.off('render', onRender)
      map.off('remove', onRemove)
      resolve(isReady)
    }
    const finishOnceFaded = (): void => {
      if (!map.loaded() || map.isMoving()) {
        isIdle = false
      } else if (isBasemapFading(map)) {
        map.triggerRepaint()
      } else {
        settle(true)
      }
    }
    const onIdle = (): void => {
      isIdle = true
      finishOnceFaded()
    }
    const onRender = (): void => {
      if (isIdle) {
        finishOnceFaded()
      }
    }
    const onRemove = (): void => settle(false)
    const timer = setTimeout(() => settle(false), timeoutMs)
    map.on('idle', onIdle)
    map.on('render', onRender)
    map.once('remove', onRemove)
    // A map with nothing dirty never renders, so never idles.
    map.triggerRepaint()
  })
}
